import { Partner } from '@/types/partner'
import { getIntegrationData } from './integrationDataUtilities'

export const supportedActionsForPartner = (partner: Partner) => {
  if (!partner) return ['form', 'webhook']

  // Define preferred order of actions in dropdown
  const actionOrder = [
    'form',
    'webhook',
    'list',
    'tag',
    'event',
    'contact',
    'automation'
  ]
  const availableFeatures = Object.keys(partner.espFeatureSupport)
    .filter((feature) => feature)
    .filter((feature) => actionOrder.includes(feature))

  availableFeatures.push('webhook')

  // Sort by preferred order
  return [...new Set(availableFeatures)].sort(
    (a, b) => actionOrder.indexOf(a) - actionOrder.indexOf(b)
  )
}

export const integrationDepedencies = (partner: Partner) => {
  // find any segment groups with sync ups
  const { segmentGroups } = useSegmentGroupStore()
  const syncUpSegmentGroups = segmentGroups.filter(
    (sg) =>
      sg.syncTo === partner.name &&
      sg.syncToCustomField &&
      sg.syncToCustomField !== ''
  )

  // find any segments with auto segments set up for the partner
  const autoSegments = segmentGroups.filter((sg) => {
    return sg.signals.some((signal) =>
      definitionLogicItems(signal.definition).some(
        (logic) => logic.$source === partner.name
      )
    )
  })

  // find any opt-in offers that explicitly reference this partner
  const { optinOffers } = useOfferStore()
  const offersUsingPartner = optinOffers.filter((o) => {
    if (!offerCanBeSubmitted(o)) return false
    const partnerName = o.triggeredAction?.settings?.partnerName
    return partnerName === partner.name
  })

  // find any opt-in offers with ESP-specific field mappings (custom*/native*)
  // these become stale when switching integrations, regardless of partnerName
  const offersWithEspFieldMappings = optinOffers.filter((o) => {
    const fields = o.triggeredAction?.settings?.fields || []
    return fields.some(
      (f: any) =>
        f.espField &&
        (f.espField.startsWith('custom*') || f.espField.startsWith('native*'))
    )
  })

  // find any flows with conditions in place that reference the partner
  const { flows } = useFlowStore()
  const flowsReferencingPartner = flows.filter((f) => {
    let found = false

    funnelDefinitionEach(f.funnelDefinition, (node) => {
      if (found) return
      if (node.type === 'yesno') {
        found = definitionLogicItems(node.condition).some(
          (logic) => logic.$source === partner.name
        )
      }
    })

    return found
  })

  return {
    syncUpSegmentGroups,
    autoSegments,
    offersUsingPartner,
    offersWithEspFieldMappings,
    flowsReferencingPartner
  }
}

export const swapOutEspPlaceholder = (partner: Partner) => {
  const { configAsString } = useConfigStore()
  if (!configAsString.includes('espplaceholder')) return

  const { flows } = useFlowStore()
  flows.forEach((f) => {
    funnelDefinitionEach(f.funnelDefinition, (node) => {
      if (node.type === 'yesno') {
        definitionLogicItems(node.condition).forEach((logic) => {
          if (logic.$source === 'espplaceholder') {
            logic.$source = partner.name
          }
        })
      }
    })
  })

  const { segmentGroups } = useSegmentGroupStore()
  segmentGroups.forEach((sg) => {
    sg.signals.forEach((signal) => {
      definitionLogicItems(signal.definition).forEach((logic) => {
        if (logic.$source === 'espplaceholder') {
          logic.$source = partner.name
        }
      })
    })
  })
}

export const customFieldName = () => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  return (
    integratedPartner?.espFeatureSupport?.customField?.label || 'Custom Field'
  )
}

/**
 * Resolves an espField value (e.g. "custom*44793", "native*email", or plain "first_name")
 * to its human-readable label by looking up the field in the integration data.
 */
export const espFieldLabel = async (espField: string): Promise<string> => {
  if (!espField) return ''

  if (espField.startsWith('custom*')) {
    return await customFieldLabel(espField.replace('custom*', ''))
  }
  if (espField.startsWith('native*')) {
    return await nativeFieldLabel(espField.replace('native*', ''))
  }
  // Plain field key (unstructured integrations)
  return espField
}

export const nativeFieldLabel = async (field: string) => {
  try {
    const data = await getIntegrationData('partner', 'native_fields')
    return data.value.find((f) => String(f.id) === field)?.label || field
  } catch (error) {
    console.warn('Failed to fetch native field label:', error)
    return field
  }
}

export const customFieldLabel = async (field: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  if (integratedPartner?.espFeatureSupport?.customField?.unstructured)
    return field

  try {
    const data = await getIntegrationData('partner', 'custom_fields')
    return data.value.find((f) => String(f.id) === field)?.label || field
  } catch (error) {
    console.warn('Failed to fetch custom field label:', error)
    return field
  }
}

export const customFieldKey = async (field: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  // For unstructured integrations, the field ID is the key
  if (integratedPartner?.espFeatureSupport?.customField?.unstructured)
    return field

  const data = await getIntegrationData('partner', 'custom_fields')
  const fieldData = data.value.find((f) => String(f.id) === String(field))
  // Return templatingToken if available (for structured integrations like Bento), otherwise the field ID
  return fieldData?.templatingToken || field
}

export const tagLabel = async (tagId: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore

  if (!integratedPartner?.espFeatureSupport?.tag) {
    return tagId
  }

  try {
    const data = await getIntegrationData('partner', 'tags')
    const tag = data.value.find((t) => String(t.id) === String(tagId))
    // Tags use 'label' property, not 'name'
    return tag?.label || tag?.name || tagId
  } catch (error) {
    console.warn('Failed to fetch tag label:', error)
    return tagId
  }
}

export const listLabel = async (listId: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore

  if (!integratedPartner?.espFeatureSupport?.list) {
    return listId
  }

  try {
    const data = await getIntegrationData('partner', 'lists')
    const list = data.value.find((l) => String(l.id) === String(listId))
    // Lists might use 'label' or 'name' depending on the integration
    return list?.label || list?.name || listId
  } catch (error) {
    console.warn('Failed to fetch list label:', error)
    return listId
  }
}
